import { Component, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from 'providers/auth.service';

/* 
Component retrieves and maintains user's cookie consent choices for usage analytics
*/

type WindowWithDataLayer = Window & {
    dataLayer: any[];
};
declare const window: WindowWithDataLayer;

@Component({
    selector: 'ce-cookie-banner',
    templateUrl: './cookie-banner.component.html',
    styleUrls: ['./cookie-banner.component.scss'],
})
export class CookieBannerComponent {
    // current website domain
    domain = window.location.hostname;
    gaDomain = environment.ga_domain;

    // observable for checking if banner has already been shown
    private readonly _cookieBannerShown: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
    cookieBannerShown$ = this._cookieBannerShown.asObservable();
    cookieBannerShown = false;

    // observable for tracking user's cookie consent choices
    private readonly _cookieConsentChoice: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
    cookieConsentChoice$ = this._cookieConsentChoice.asObservable();
    cookieConsentChoice = undefined;

    isLoginFired = false;

    constructor(
        private cdRef: ChangeDetectorRef,
        private authService: AuthService
    ) {}

    // checks for user authentication before cookie banner is displayed
    isUserAuthed() {
        if (this.authService.authenticated$.value) {
            return true;
        }
        return false;
    }

    // updates value for cookie banner shown or not
    setShowBanner(isShown: boolean) {
        this._cookieBannerShown.next(isShown);
        this.cookieBannerShown = isShown;
        return this.cookieBannerShown;
    }

    // send user consent choice for GA consent mode
    sendConsent(choice: string) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            consent: 'consent',
            update: 'update',
            storage: {
                ad_storage: choice,
                analytics_storage: choice,
            },
        });
    }

    // updates user's consent choice
    updateConsentChoice(choice: boolean) {
        this._cookieConsentChoice.next(choice);
        this.cookieConsentChoice = choice;
        this.storeConsentChoice();

        const send_value = choice === true ? 'granted' : 'denied';
        this.sendConsent(send_value);

        // remove GA cookies if user declines consent
        if (send_value === 'denied') {
            this.removeCookie();
        }
        window.location.reload();
    }

    // stores consent choice as browser cookie
    storeConsentChoice() {
        if (this.cookieConsentChoice !== undefined) {
            const cookieName = 'CE usage analytics';

            const cookieData = {
                choice: this.cookieConsentChoice,
            };

            // cookie expiration set to one year from today (due to ePrivacy directive), in milliseconds
            const cookieTimeOffset = 365 * 24 * 60 * 60 * 1000;

            // set cookie to browser
            const isHttps = window.location.protocol === 'https:';
            document.cookie =
                `${cookieName}=${JSON.stringify(cookieData)}; expires=${new Date(Date.now() + cookieTimeOffset).toUTCString()}; path=/; domain=${this.domain}; SameSite=Strict;` +
                (isHttps ? 'Secure;' : '');
            this.setShowBanner(false);
        }
    }

    disableWrongEnvCookie() {
        if (environment.production) {
            window[environment.gaDevContainer] = true;
        } else {
            window[environment.gaProdContainer] = true;
        }
    }

    // removes Google Analytics cookies from browser
    removeCookie() {
        // disable GA so that cookies can be removed
        window[environment.gaDevContainer] = true;
        window[environment.gaProdContainer] = true;
        // set expiry date of cookies in the past to remove immediately
        document.cookie = `${environment.gaTagId_env}=; expires=Sat, 01 Jan 2000 00:00:00 UTC; path=/; domain=${this.gaDomain};`;
        document.cookie = `${environment.gaTagId_generic}=; expires=Sat, 01 Jan 2000 00:00:00 UTC; path=/; domain=${this.gaDomain};`;
        this.cdRef.detectChanges();
    }

    // checks value of user's choice in browser cookies
    readConsentChoice() {
        const cookieName = 'CE usage analytics';
        const name = cookieName + '=';

        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');

        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(name) === 0) {
                const cookieValue = cookie.substring(name.length, cookie.length);
                try {
                    this.setShowBanner(true);
                    return JSON.parse(cookieValue).choice; // return value of "choice" from browser cookies
                } catch (error) {
                    console.error('Error parsing cookie data:', error);
                }
            }
        }
        this.setShowBanner(false); // set cookie banner as not shown
        return undefined;
    }

    // sends login event
    setLoginFired() {
        this.isLoginFired = true;
    }
}
